import { Badge, HStack, Tooltip } from '@chakra-ui/react'
import {
  type Icon,
  ChartLineUp,
  Crown,
  GraduationCap,
  Lightning,
  Robot,
  Shapes,
  ShoppingBag,
  Sparkle,
  Star,
  Storefront,
  Trophy,
  UserCircle,
} from '@phosphor-icons/react'

/**
 * Tags de "Planos e Acessos" do vendedor.
 *
 * A lista vem pronta da API (`accessTags` / `ownerTags`), que espelha o
 * catálogo do admin — o front não mantém uma segunda lista de flags, que sairia
 * de sincronia na primeira flag nova.
 */
export interface AccessTag {
  key: string
  label: string
  group: 'plan' | 'feature'
  color: string
}

const TAG_ICONS: Record<string, Icon> = {
  isElite: Crown,
  isGold: Star,
  isMembroBlack: Crown,
  isLightShopee: ShoppingBag,
  isGuilhermeCirilo: UserCircle,
  isMariaMonteiro: Sparkle,
  isMentoria20k: GraduationCap,
  isArena: Trophy,
  isProUser: Sparkle,
  isGoldUser: Sparkle,
  isWeBot: Robot,
  isMontagemDeLojaActive: Storefront,
  isFastAdActive: Lightning,
  isAdsInsightsActive: ChartLineUp,
  isKitAdGeneratorActive: Shapes,
}

const SIZES = {
  xs: { fontSize: '9px', px: 1, py: 0, icon: 9 },
  sm: { fontSize: '2xs', px: 1.5, py: 0.5, icon: 10 },
  md: { fontSize: 'xs', px: 2, py: 1, icon: 12 },
} as const

export default function AccessTags({
  tags,
  size = 'sm',
  max,
  withIcons = false,
  showDefault = false,
  justify,
}: {
  tags?: AccessTag[] | null
  size?: keyof typeof SIZES
  /** Mostra só as N primeiras e um "+N" com o resto no tooltip. */
  max?: number
  withIcons?: boolean
  /** Sem nenhum plano, mostra o selo "Plano Padrão". */
  showDefault?: boolean
  justify?: 'center' | 'flex-start'
}) {
  const list = tags || []
  const s = SIZES[size]

  if (list.length === 0) {
    if (!showDefault) return null
    return (
      <HStack spacing={1} flexWrap="wrap" justify={justify}>
        <Badge
          colorScheme="gray"
          variant="subtle"
          fontSize={s.fontSize}
          px={s.px}
          py={s.py}
        >
          Plano Padrão
        </Badge>
      </HStack>
    )
  }

  const visible = max ? list.slice(0, max) : list
  const hidden = max ? list.slice(max) : []
  const hasPlan = list.some((tag) => tag.group === 'plan')

  return (
    <HStack spacing={1} flexWrap="wrap" justify={justify} rowGap={1}>
      {showDefault && !hasPlan && (
        <Badge
          colorScheme="gray"
          variant="subtle"
          fontSize={s.fontSize}
          px={s.px}
          py={s.py}
        >
          Plano Padrão
        </Badge>
      )}
      {visible.map((tag) => {
        const Icon = withIcons ? TAG_ICONS[tag.key] : undefined
        return (
          <Badge
            key={tag.key}
            colorScheme={tag.color}
            /* Funcionalidade contratada à parte fica com contorno, para não se
               confundir com o plano do vendedor. */
            variant={tag.group === 'feature' ? 'outline' : 'subtle'}
            fontSize={s.fontSize}
            px={s.px}
            py={s.py}
            borderRadius="full"
            textTransform="none"
            maxW="100%"
          >
            {Icon ? (
              <HStack spacing={1} as="span">
                <Icon size={s.icon} weight="fill" />
                <span>{tag.label}</span>
              </HStack>
            ) : (
              tag.label
            )}
          </Badge>
        )
      })}
      {hidden.length > 0 && (
        <Tooltip label={hidden.map((tag) => tag.label).join(', ')} hasArrow>
          <Badge
            colorScheme="gray"
            variant="subtle"
            fontSize={s.fontSize}
            px={s.px}
            py={s.py}
            borderRadius="full"
            textTransform="none"
          >
            +{hidden.length}
          </Badge>
        </Tooltip>
      )}
    </HStack>
  )
}
